import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { useToast } from "../components/Toast.js";
import type { TenantCustomer, TenantCustomerListItem } from "../lib/customers.js";

/**
 * Tenant admin — Create new customer (ADR-0075 §3.3).
 *
 * Route: `/app/admin/customers/new`. One of the rare pages with an
 * explicit Create button (autosave is the default elsewhere) — there's no
 * server-side row to write into until creation completes.
 */
export function CustomerNew() {
  const nav = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const initialParent = searchParams.get("parent_id") ?? "";

  // The form binds to `external_id` on the wire (DB column unchanged) but the
  // user-facing label is "Account Code".
  const [externalId, setExternalId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [parentId, setParentId] = useState<string>(initialParent);
  const [parents, setParents] = useState<TenantCustomerListItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await apiFetch<{ customers: TenantCustomerListItem[] }>(
          "/api/tenant/admin/customers"
        );
        setParents(res.customers);
      } catch {
        // Non-fatal — the parent picker just shows empty.
      }
    })();
  }, []);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<{ customer: TenantCustomer }>("/api/tenant/admin/customers", {
        method: "POST",
        body: {
          external_id: externalId.trim(),
          display_name: displayName.trim(),
          parent_customer_id: parentId === "" ? null : parentId
        }
      });
      toast.push({ kind: "success", message: "Customer created." });
      nav(`/app/admin/customers/${res.customer.id}`);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = externalId.trim().length > 0 && displayName.trim().length > 0 && !busy;

  return (
    <PageLayout title="New customer">
      <div className="card">
        <div style={{ display: "grid", gap: 10, maxWidth: 640 }}>
          <label className="field">
            <span className="label">Display name</span>
            <input
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Acme Corp"
            />
          </label>
          <label className="field">
            <span className="label">Account Code</span>
            <input
              className="input"
              type="text"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="ACME"
            />
            <div className="help">
              Stable, per-tenant key — your internal customer-master code (e.g. <code>DEX010</code>).
              Used to match watch rows to this customer.
            </div>
          </label>
          <label className="field">
            <span className="label">Parent customer</span>
            <select
              className="select"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">(none — top-level)</option>
              {parents.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
            <div className="help">
              Optional. Sub-customers inherit role-typed user mappings from their ancestors when a slot
              has no direct match.
            </div>
          </label>

          {error && <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>}
          <div className="actions">
            <button className="btn" onClick={() => nav("/app/admin/customers")} disabled={busy}>
              Cancel
            </button>
            <button
              className="btn btnPrimary"
              onClick={() => void submit()}
              disabled={!canSubmit}
            >
              Create
            </button>
          </div>
          <div className="help muted">
            To make this customer the catchall, create it first, add a primary mapped user on its detail
            page, then enable the Catchall toggle.
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
